import { useForm } from "react-hook-form";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import useDisableBodyScroll from "helpers/DisableBodyScroll";
import { fileToBase64, getExtension, getParent } from "helpers/utils";
import {
  PRODUCT_CATEGORY_COLLECTION_QUERY,
  PRODUCT_OPTION_COLLECTION_QUERY,
} from "modules/Products/Services/Queries/Queries";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_PRODUCT_LIST,
  UPDATE_PRODUCT_LIST,
} from "modules/Products/Services/Mutations/Mutation";
import ProductsListEditModalOptionElement from "./ProductsListEditModalOptionElement";
import ProductsListEditModalCreateElement from "./ProductsListEditModalCreateElement";
import { LoadingDesign } from "helpers";

export interface FormType {
  logo: any;
  name: string;
  productCategory: string | number;
  priceMethod: string;
  priceOffset: string | number;
  description: string;
  customId: string;
  threshold: string | number;
  turnAround: string | number;
  rushTurnAround: boolean;
  blankTurnAround: string | number;
  directSaleDiscount: string | number;
  isSingle: boolean;
  productOption: any;
}

const ProductsListEditModal: FC<{
  showEditModal: boolean;
  setShowEditModal: Function;
  editProductList?: number;
  productCategoriesId: { id: number; name: string } | undefined;
  product:
    | {
        coverImageFile: string;
        coverImageName: string;
        coverImageUid: string;
        description: any;
	    customId: string | null;
        id: number;
        link: any;
        name: string | null;
        price: string | null;
        priceMethod: string | null;
        priceOffset: string | null;
        threshold: number | null;
        turnAround: number | null;
        rushTurnAround: boolean | null;
        blankTurnAround?: number | null;
        directSaleDiscount: number | null;
        isSingle: boolean | null;
        productCategory: {
          id: number;
          name: string | null;
        };
        productOptionsProducts: any[];
      }
    | undefined;
  productLoading: boolean;
  productError?: string;
  refetchProductListCategorywise: Function;
}> = ({
  showEditModal,
  setShowEditModal,
  editProductList,
  productCategoriesId,
  product,
  productLoading,
  productError,
  refetchProductListCategorywise,
}) => {
  const [deleteCoverImage, setDeleteCoverImage] = useState(false);
  const { data: productCategoriesList, loading: categoriesLoading, error: categoriesError } = useQuery(
    PRODUCT_CATEGORY_COLLECTION_QUERY,
    {
      variables: {
        requiredFields: ["id", "name"],
      },
    },
  );
  const { data: productOptionList, loading: optionsLoading, error: optionsError } = useQuery(
    PRODUCT_OPTION_COLLECTION_QUERY,
    {
      skip: !editProductList,
      fetchPolicy: "network-only",
      variables: {
        requiredFields: ["id", "title"],
      },
    },
  );
  const [createProductList] = useMutation(CREATE_PRODUCT_LIST);
  const [updateProductList] = useMutation(UPDATE_PRODUCT_LIST);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm<FormType>();
  const [file, setFile] = useState<string>();
  const [fileName, setFileName] = useState<string>();
  const [initializedProduct, setInitializedProduct] = useState<number | "new">();
  const loadError = productError || categoriesError?.message || optionsError?.message;
  const isReady = !productLoading && !categoriesLoading && !optionsLoading && !loadError
    && initializedProduct === (editProductList ?? "new")
    && (!editProductList || product?.id === editProductList);

  useEffect(() => {
    if (editProductList && (productLoading || product?.id !== editProductList)) {
      setInitializedProduct(undefined);
      return;
    }
    const currentProduct = editProductList ? product : undefined;
    // Replace the entire form, including unchecked options and empty fields.
    reset({
      logo: "",
      name: currentProduct?.name ?? "",
      productCategory: currentProduct?.productCategory?.id ?? productCategoriesId?.id ?? "",
      priceMethod: currentProduct?.priceMethod?.toLowerCase() === "fixed" ? "Fixed"
        : currentProduct?.priceMethod?.toLowerCase() === "table" ? "Table" : "",
      priceOffset: currentProduct?.priceOffset ?? "",
      description: currentProduct?.description ?? "",
      customId: currentProduct?.customId ?? "",
      threshold: currentProduct?.threshold ?? "",
      turnAround: currentProduct?.turnAround ?? "",
      rushTurnAround: currentProduct?.rushTurnAround ?? false,
      blankTurnAround: currentProduct?.blankTurnAround ?? "",
      directSaleDiscount: currentProduct?.directSaleDiscount ?? "",
      isSingle: currentProduct?.isSingle ?? false,
      productOption: Object.fromEntries((currentProduct?.productOptionsProducts ?? [])
        .filter((option) => option.productOption?.id)
        .map((option) => [option.productOption.id, true])),
    });
    setDeleteCoverImage(false);
    setFileName(currentProduct?.coverImageName || undefined);
    if (currentProduct?.coverImageFile && currentProduct?.coverImageName) {
      setFile(
        `data:image/${
          getExtension(currentProduct.coverImageName) === "svg"
            ? "svg+xml"
            : getExtension(currentProduct.coverImageName)
        };base64,${currentProduct.coverImageFile}`,
      );
    } else {
      setFile(undefined);
    }
    setInitializedProduct(editProductList ?? "new");
  }, [product, productLoading, editProductList, productCategoriesId?.id, reset]);

  const onUploadFileChange = (event: any) => {
    if (event?.target?.files[0]?.size > 1e7) {
      if (product?.coverImageFile && product?.coverImageName) {
        setFile(
          `data:image/${getExtension(
            product?.coverImageName,
          )};base64,${product?.coverImageFile}`,
        );
        setFileName(product?.coverImageName);
        setValue("logo", "");
      } else {
        setValue("logo", "");
        setFile(undefined);
        setFileName(undefined);
      }
      return alert("Maximum file size limit is 1MB.");
    } else {
      fileToBase64(event?.target?.files[0], (err: any, result: any) => {
        if (result) {
          setFile(result);
          setFileName(event.target.files[0]?.name);
        }
      });
    }
    if (event.target.files < 1 || !event.target.validity.valid) {
      return;
    }
  };
  const [loading, setLoading] = useState(false);
  const productlistEditFormHandler = (data: any) => {
    if (!isReady || loading) return;
    if (editProductList) {
      let productOptionIds: number[] = [];
      Object.entries(data.productOption ?? {}).map(([key, value], index) => {
        if (value === true) {
          productOptionIds.push(+key);
        }
        return null;
      });
      setLoading(true);
      updateProductList({
        variables: {
          id: editProductList,
          coverImageFile: file && data.logo[0]?.name ? file : undefined,
          coverImageFileName:
            file && data.logo[0]?.name ? data.logo[0]?.name : undefined,
          name: data?.name,
          productCategoryId: +data?.productCategory,
          priceMethod: data?.priceMethod,
          priceOffset: +data?.priceOffset,
          description: data?.description ? data?.description : undefined,
	      customId: data?.customId ? data?.customId : undefined,
          productOptionsIds: productOptionIds,
          deleteCoverImage: file ? deleteCoverImage : undefined,
          threshold: data?.threshold !== "" ? +data.threshold : undefined,
          turnAround: data?.turnAround !== "" ? +data.turnAround : undefined,
          rushTurnAround: data?.rushTurnAround ?? false,
          // An empty field clears the value (null), unlike omitting it
          blankTurnAround:
            data.blankTurnAround === "" || data.blankTurnAround == null
              ? null
              : +data.blankTurnAround,
          directSaleDiscount:
            data?.directSaleDiscount !== "" ? +data.directSaleDiscount : undefined,
          isSingle: data?.isSingle ?? false,
        },
      })
        .then(() => {
          setLoading(false);
          setShowEditModal(false);
          refetchProductListCategorywise();
          setValue("logo", "");
          setFile(undefined);
          setFileName(undefined);
        })
        .catch((errors: any) => {
          setLoading(false);
          // alert(error?.message);(errors?.message);
        });
    } else {
      setLoading(true);
      createProductList({
        variables: {
          coverImageFile: file && data.logo[0]?.name ? file : undefined,
          coverImageFileName:
            file && data.logo[0]?.name ? data.logo[0]?.name : undefined,
          name: data?.name ? data?.name : undefined,
          productCategoryId: +data?.productCategory
            ? +data?.productCategory
            : undefined,
          priceMethod: data?.priceMethod ? data?.priceMethod : undefined,
          priceOffset:
            data?.priceOffset === ""
              ? undefined
              : data?.priceOffset
                ? +data?.priceOffset
                : undefined,
          description: data?.description ? data?.description : undefined,
		  customId: data?.customId ? data?.customId : undefined,
          threshold: data?.threshold ? +data.threshold : undefined,
          turnAround: data?.turnAround ? +data.turnAround : undefined,
          rushTurnAround: data?.rushTurnAround ?? false,
          blankTurnAround:
            data?.blankTurnAround === "" || data?.blankTurnAround == null
              ? undefined
              : +data.blankTurnAround,
          directSaleDiscount: data?.directSaleDiscount
            ? +data.directSaleDiscount
            : undefined,
          isSingle: data?.isSingle ?? false,
        },
      })
        .then(() => {
          setLoading(false);
          setShowEditModal(false);
          refetchProductListCategorywise();
          setValue("logo", "");
          setFile(undefined);
          setFileName(undefined);
        })
        .catch((errors: any) => {
          setLoading(false);
          // alert(error?.message);(errors?.message);
        });
    }
  };

  useDisableBodyScroll(showEditModal);

  const productOptions = useMemo(() => {
    const productsOptionConvertionHandler: any = (
      productOptions: {
        id: number;
        title: string;
        children: [] | any[];
      }[],
    ) => {
      if (productOptions?.length === 0) return null;

      return productOptions?.map(
        (productOptions: {
          id: number;
          title: string;
          children: [] | any[];
        }) => {
          return {
            id: productOptions?.id,
            name: productOptions?.title,
            children: productsOptionConvertionHandler(productOptions?.children)
              ? productsOptionConvertionHandler(productOptions?.children)
              : [],
          };
        },
      );
    };
    return productsOptionConvertionHandler(productOptionList?.productOptions) ?? [];
  }, [productOptionList]);

  const productCategories = productCategoriesList?.productCategories ?? [];

  let selectOptionsProductCategories: any[] = [];
  const getSelectOptionsHandler = (treeData: any[]) => {
    if (treeData?.length === 0) return null;
    treeData?.map((treeData) => {
      selectOptionsProductCategories.push({
        id: treeData?.id,
        name: `${
          getParent(productCategories, treeData?.id)?.length === 1 ? "" : ""
        }${
          getParent(productCategories, treeData?.id)?.length === 2 ? "- " : ""
        }${
          getParent(productCategories, treeData?.id)?.length === 3 ? "- - " : ""
        }${treeData?.name}`,
      });
      if (treeData?.children?.length > 0) {
        getSelectOptionsHandler(treeData?.children);
      }
      return treeData;
    });
  };
  getSelectOptionsHandler(productCategories);
  return (
    <div
      className={`fixed inset-0 bg-transparent z-50 p-2 flex flex-row ${
        editProductList ? "" : "lg:items-center"
      } justify-center w-full`}
    >
      <div
        className="fixed inset-0 bg-black/60 -z-20"
        onClick={() => {
          if (!loading) {
            setShowEditModal(false);
          }
        }}
      />
      <div
        className={`${
          editProductList ? "" : "w-[340px] sm:w-[560px] mx-auto"
        }  bg-[#E4E6E9] shadow-[0_0_64px_#000] h-fit`}
      >
        {loadError ? (
          <div className="p-4" role="alert">
            <p>Unable to load this product: {loadError}</p>
            <button type="button" onClick={() => setShowEditModal(false)}>Close</button>
          </div>
        ) : !isReady ? (
          <div className="w-[300px] sm:w-[400px] lg:w-[600px]">
            <LoadingDesign />
          </div>
        ) : (
          <Fragment>
            <h3 className="bg-[#0078AE] text-[#000] font-semibold flex justify-between py-2 px-2">
              <span className="text-[16px] leading-5">
                {editProductList ? "Edit Product" : "New Product"}
              </span>
              <span className="inline-flex justify-center items-center text-red-600">
                <i
                  onClick={() => {
                    if (!loading) {
                      setShowEditModal(false);
                    }
                  }}
                  className="icon-cancel cursor-pointer text-white"
                />
              </span>
            </h3>
            <form
              onSubmit={handleSubmit(productlistEditFormHandler)}
              className={`${
                editProductList
                  ? "grid grid-cols-1 lg:grid-cols-2 max-h-[93vh] overflow-y-auto"
                  : ""
              }`}
            >
              <ProductsListEditModalCreateElement
                deleteCoverImage={deleteCoverImage}
                editProductList={editProductList}
                errors={errors}
                file={file}
                fileName={fileName}
                onUploadFileChange={onUploadFileChange}
                register={register}
                selectOptionsProductCategories={selectOptionsProductCategories}
                setDeleteCoverImage={setDeleteCoverImage}
                product={product}
                loading={loading}
              />
              <ProductsListEditModalOptionElement
                editProductList={editProductList}
                productOptions={productOptions}
                register={register}
                loading={loading}
              />
            </form>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default ProductsListEditModal;
